import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, X } from "lucide-react";
import UniversalPhotoUploadDialog from "@/components/common/UniversalPhotoUploadDialog";
import SecureImage from "@/components/SecureImage";
import { useTranslation } from "react-i18next";

interface ImageUploaderProps {
  imageFile: File | null;
  imagePreview: string | null;
  onImageChange: (file: File) => void;
  required?: boolean;
  disabled?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  imageFile, 
  imagePreview, 
  onImageChange,
  required = false,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  const handlePhotoUploaded = async (photoUrl: string) => {
    // Convert the uploaded URL back to a File object for compatibility with existing code
    try {
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      const filename = `photo_${Date.now()}.${blob.type.split('/')[1] || 'jpg'}`;
      const file = new File([blob], filename, { type: blob.type });
      onImageChange(file);
      setPhotoDialogOpen(false);
    } catch (error) {
      console.error('Error converting photo URL to File:', error);
      // Fallback - still close dialog and handle the URL somehow
      setPhotoDialogOpen(false);
    }
  };

  const handleRemoveImage = () => {
    // Create an empty file to clear the image
    const emptyFile = new File([], '', { type: 'image/jpeg' });
    onImageChange(emptyFile);
  };

  return (
    <div className="space-y-2">
      <label htmlFor="image" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
        Fotoğraf
      </label>

      {/* Image Preview */}
      {imagePreview && (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-gray-200 bg-gray-50 mb-2">
          <SecureImage
            src={imagePreview}
            alt="Giriş fotoğrafı"
            className="w-full h-full object-cover"
          />
          {!disabled && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 rounded-full shadow-sm"
              onClick={handleRemoveImage}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      {/* Add/Change Photo Button */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setPhotoDialogOpen(true)}
        disabled={disabled}
        className="w-full flex items-center justify-center py-5 border-2 border-dashed border-gray-200 bg-white text-gray-500 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-25 rounded-xl"
      >
        <ImageIcon className="h-4 w-4 mr-2" />
        <span className="text-[13px] font-medium">{imagePreview ? 'Fotoğrafı Değiştir' : 'Fotoğraf Ekle'}</span>
      </Button>

      {/* Photo Upload Dialog */}
      <UniversalPhotoUploadDialog
        isOpen={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onPhotoUploaded={handlePhotoUploaded}
        bucketName="documents"
        folderPath="entries"
        title="Giriş Fotoğrafı Ekle"
        description="Giriş için bir fotoğraf yükleyebilirsiniz."
        disabled={disabled}
      />

      {required && (
        <input
          type="hidden"
          required={required && !imagePreview}
        />
      )}
    </div>
  );
};

export default ImageUploader;